import { useEffect, useMemo, useState } from "react";
import useAxios from "../utils/useAxios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useRef } from "react";
import { useAppContext } from "../context/AppContext";
import AsyncSelect from "react-select/async";
import { useNavigate } from "react-router-dom";
import { currentDateAndTime } from "../utils/generateCurrentDateAndTime";
import Loader from "../components/common/Loader";
import { hideModal, showModal } from "../utils/modalUtility";
import Pagination from "../components/common/Pagination";
import { useAgencies } from "../hooks/useAgencies";

function RecurringDonation() {
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [recurringDonations, setRecurringDonations] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [receiptDetails, setReceiptDetails] = useState({});
  const [role, setRole] = useState(null);
  const [view, setView] = useState("excel"); // default: show Excel section
  const [downloading, setDownloading] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [uploadRows, setUploadRows] = useState([]);

  const [donationLoading, setDonationLoading] = useState(false);

  const [followUpRemarkDataById, setFollowUpRemarkDataById] = useState({
    followUpRemark: [],
  });

  const [followUpRemarkData, setFollowUpRemarkData] = useState([]);

  const [isFollowUpEditMode, setFollowUpEditMode] = useState(false);

  const { agencies, loading: agenciesLoading } = useAgencies();

  const { state } = useAppContext();
  const { data, loading: donorLoading } = state;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    user_id: "",
    transaction_date_and_time: "",
    amount: "",
    reasonDescription: "",
    reasonCode: "",
    tr_no: "",
    normal: "yes", // Default selected
    agency_wise: "no",
  });

  const [filters, setFilters] = useState({
    search: "",
    agency: "",
    from_date: "",
    to_date: "",
    status: "",
  });

  const [followUpData, setFollowUpData] = useState({
    year: "",
    from_date: "",
    to_date: "",
    normal: "yes",
    agency_wise: "no",
  });

  const filteredOptions = useMemo(() => {
    return data
      .filter((user) => {
        const nameMatch = user.full_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());
        const phoneMatch = user.phone_number
          ?.toString()
          .includes(searchTerm.toLowerCase());
        return nameMatch || phoneMatch;
      })
      .slice(0, 50)
      .map((user) => ({
        value: user.id,
        label: `${user.full_name} - ${user.phone_number || ""}`,
      }));
  }, [data, searchTerm]);

  const loadOptions = (inputValue, callback) => {
    setSearchTerm(inputValue);
    callback(filteredOptions);
  };

  useEffect(() => {
    if (formData.user_id) {
      const option =
        filteredOptions.find((opt) => opt.value === formData.user_id) || null;
      setSelectedUser(option);
    } else {
      setSelectedUser(null);
    }
  }, [formData.user_id, filteredOptions]);

  const handleFilterChange = (key, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const [excelFile, setExcelFile] = useState(null);
  const [message, setMessage] = useState("");

  const handleResetFilters = () => {
    setFilters({
      agency: "",
      search: "",
      from_date: "",
      to_date: "",
      status: "",
    });
    setPage(1);
    setShouldFetch(true);
  };

  useEffect(() => {
    if (shouldFetch) {
      recurringDonation();
      setShouldFetch(false);
    }
  }, [shouldFetch]);

  const removeFormData = () => {
    setFormData({
      user_id: "",
      transaction_date_and_time: "",
      amount: "",
      reasonDescription: "",
      reasonCode: "",
      tr_no: "",
    });
  };

  const axiosAPI = useAxios();
  const receiptRef = useRef();
  const fileInputRef = useRef();

  function followUpRemarkHandleChange(e) {
    const { name, value } = e.target;

    setFollowUpRemarkData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  const handleExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (
      file &&
      file.type !==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      alert("Please upload a valid Excel file (.xlsx)");
      setExcelFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    } else {
      setExcelFile(file);
    }
  };

  const buildQuery = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    if (formData.normal) params.append("normal", formData.normal);
    if (formData.agency_wise)
      params.append("agency_wise", formData.agency_wise);

    params.append("page", page);
    return params.toString();
  };

  const downloadExcel = async (table_name) => {
    setDownloading(true);
    const queryString = buildQuery();
    const { agency_wise: is_agency_wise, normal } = formData;

    try {
      const response = await axiosAPI.get(
        `/api/recurring-donation/download-excel?table=${table_name}&${queryString}`,
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200) {
        throw new Error("Failed to download file");
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${table_name}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download file. Please try again later.");
    } finally {
      setDownloading(false);
    }
  };

  const handleAgencyWiseSubmit = async (e) => {
    e.preventDefault();

    const { normal, agency_wise } = formData;
    const { search, from_date, to_date } = filters;
    const hasFilters = search || agency_wise || (from_date && to_date);

    if (normal === "yes") {
      if (hasFilters) {
        downloadExcel("recurring_donations", filters);
      } else {
        const confirm = window.confirm(
          "No filters applied. Do you want to download the full sheet (Normal)?",
        );
        if (confirm) {
          downloadExcel("recurring_donations");
        }
      }
    }

    if (agency_wise === "yes") {
      if (hasFilters) {
        downloadExcel("recurring_donations", filters);
      } else {
        const confirm = window.confirm(
          "No filters applied. Do you want to download the full sheet (Agency-wise)?",
        );
        if (confirm) {
          downloadExcel("recurring_donations");
        }
      }
    }

    closeModal();
  };

  const downloadFollowUpExcel = async (table_name, followUpData) => {
    setDownloading(true);

    try {
      const params = new URLSearchParams({
        table: table_name,
        ...followUpData,
      }).toString();

      const response = await axiosAPI.get(
        `/api/recurring-donation/download-follow-up-excel?${params}`,
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200) {
        throw new Error("Failed to download file");
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${table_name}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      hideModal("followUpModal");
      setFollowUpData({
        year: "",
        from_date: "",
        to_date: "",
      });
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download file. Please try again later.");
    } finally {
      setDownloading(false);
    }
  };

  const handleFollowUpSubmit = async (e) => {
    e.preventDefault();

    const { year, from_date, to_date } = followUpData;

    if (!year || !from_date || !to_date) {
      alert("Please fill all the required fields.");
      return;
    }

    downloadFollowUpExcel("recurring_donations", followUpData);
    closeModal();
  };

  const handleExcelSubmit = async (e) => {
    e.preventDefault();

    if (!excelFile) {
      alert("Please select an Excel file.");
      return;
    }

    const formData = new FormData();
    formData.append("excel", excelFile);
    formData.append("upload_date_and_time", currentDateAndTime());

    try {
      const response = await axiosAPI.post(
        "/api/recurring-donation/upload-excel",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const {
        message = "",
        insertedCount = 0,
        rows = [],
        failedExcelBase64,
      } = response.data;

      if (failedExcelBase64) {
        const binaryString = atob(failedExcelBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "failed_rows.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setUploadRows(rows);
      const failedRows = rows.filter((r) => r.status === "fail");

      if (rows.length === 0) {
        window.notifyMessage(
          "Error",
          "No valid data found in Excel file.",
          "error",
        );
      } else if (insertedCount === 0 && failedRows.length > 0) {
        window.notifyMessage(
          "Upload Failed",
          `No rows were inserted.\nFailed Rows: ${failedRows.length}`,
          "error",
        );
        console.table(failedRows);
      } else if (failedRows.length > 0) {
        window.notifyMessage(
          "Partial Success",
          `${message}\nInserted: ${insertedCount}, Failed: ${failedRows.length}`,
          "warning",
        );
        console.table(failedRows);
      } else {
        window.notifyMessage(
          "Success",
          "Excel file uploaded successfully!",
          "success",
        );
      }

      recurringDonation();

      if (fileInputRef.current) fileInputRef.current.value = null;

      setExcelFile(null);
      setMessage("");
    } catch (err) {
      console.error("Excel Upload Error:", err);

      const errorMsg =
        err?.response?.data?.message ||
        "Something went wrong while uploading the Excel file.";

      window.notifyMessage("Error", errorMsg, "error");

      if (err.response?.data?.error) {
        console.error("Backend Error:", err.response.data.error);
      }
    }
  };

  async function recurringDonation() {
    setLoading(true);
    const queryString = buildQuery();

    try {
      const res = await axiosAPI.get(
        `/api/recurring-donation/get-recurring-donation?${queryString}`,
      );
      setRecurringDonations(res.data.results);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error("Error fetching recurring donations:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    recurringDonation();
  }, []);

  useEffect(() => {
    recurringDonation();
  }, [page]);

  useEffect(() => {
    setRole(state?.user?.role);
  }, []);

  function fetchCurrentDateAndTime() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");

    const formattedDateTime = `${year}-${month}-${day} ${hours}:${minutes}`;

    setFormData((prev) => ({
      ...prev,
      transaction_date_and_time: formattedDateTime,
    }));
  }

  useEffect(() => {
    fetchCurrentDateAndTime();
  }, []);

  const handlePageClick = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setPage(pageNum);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      user_id: formData.user_id?.value,
      transaction_date_and_time: formData.transaction_date_and_time,
      amount: parseInt(formData.amount, 10),
    };

    setDonationLoading(true);
    try {
      let response;
      if (isEditMode) {
        response = await axiosAPI.put(
          `/api/recurring-donation/update-donation/${currentUserId}`,
          payload,
        );
        window.notifyMessage(
          "Success",
          "Donation updated successfully",
          "success",
        );
        setRecurringDonations((prevDonations) =>
          prevDonations.map((donation) =>
            donation.id === currentUserId
              ? {
                  ...donation,
                  user_id: formData.user_id,
                  transaction_date_and_time: formData.transaction_date_and_time,
                  amount: formData.amount,
                  reason_description: formData.reasonDescription,
                  reason_code: formData.reasonCode,
                  tr_no: formData.tr_no,
                }
              : donation,
          ),
        );
      } else {
        response = await axiosAPI.post(
          "/api/recurring-donation/add-donation",
          payload,
        );
        window.notifyMessage(
          "Success",
          "Donation added successfully",
          "success",
        );
      }
      hideModal("newUserModal");
      recurringDonation();
      removeFormData();
      setDonationLoading(false);
      setIsEditMode(false);
      closeModal();
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        window.notifyMessage("Error", error.response.data.message, "danger");
      } else {
        window.notifyMessage("Error", "There was an error!", "danger");
      }
    } finally {
      setDonationLoading(false);
    }
  };

  const handleFollowUpRemarkSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      user_id: currentUserId,
      description: followUpRemarkData.followUpRemark,
    };

    try {
      let response;

      if (isFollowUpEditMode) {
        response = await axiosAPI.put(
          `/api/follow-up-remarks/${currentUserId}`,
          payload,
        );

        window.notifyMessage(
          "Success",
          "Follow-up remark updated successfully",
          "success",
        );
      } else {
        response = await axiosAPI.post(
          "/api/donor/add-follow-up-remarks",
          payload,
        );

        window.notifyMessage(
          "Success",
          "Follow-up remark added successfully",
          "success",
        );
      }

      setFollowUpEditMode(false);
      setFollowUpRemarkData((prev) => ({ ...prev, followUpRemark: "" }));
      hideModal("giftModal");
    } catch (error) {
      if (error.response?.data?.message) {
        window.notifyMessage("Error", error.response.data.message, "danger");
      } else {
        window.notifyMessage("Error", "There was an error!", "danger");
      }
    }
  };

  const openEditModal = (id) => {
    setIsEditMode(true);
    const selected_recurrer_donation = recurringDonations.find(
      (donor) => donor.id === id,
    );

    setCurrentUserId(id);

    if (selected_recurrer_donation) {
      const selectedUser = data.find(
        (user) => user.id === selected_recurrer_donation.user_id,
      );

      setFormData({
        user_id: selectedUser
          ? { value: selectedUser?.id, label: selectedUser?.full_name }
          : null,
        transaction_date_and_time:
          selected_recurrer_donation.transaction_date_and_time,
        amount: selected_recurrer_donation.amount,
        reasonDescription: selected_recurrer_donation.reason_description,
        reasonCode: selected_recurrer_donation.reason_code,
        tr_no: selected_recurrer_donation.tr_no,
      });
    }

    showModal("newUserModal");
  };

  const openUploadDonorModal = () => {
    showModal("newUserExcelModal");
  };

  const downloadPdf = () => {
    const input = receiptRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`DonationReceipt_${receiptDetails?.full_name || "User"}.pdf`);
    });

    setShowPdfModal(false);
    setReceiptDetails("");
  };

  const handleDelete = async (id) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this?");
    if (!isConfirmed) return;

    try {
      const response = await axiosAPI.delete(
        `/api/notification/delete-notification/${id}`,
      );
      window.notifyMessage("Success", "Deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting:", error);
      window.notifyMessage(
        "Error",
        "An error occurred while deleting.",
        "danger",
      );
    }
  };

  function openAgencyWiseModal() {
    showModal("agencyWiseModal");
  }
  function followUpModal() {
    showModal("followUpModal");
  }

  const closeModal = () => {
    setIsEditMode(false);
    removeFormData();
    setUploadRows("");
    setCurrentUserId(null);
  };

  const fetchFollowUpRemarkDataById = async (id) => {
    try {
      const response = await axiosAPI.get(`/api/donor/follow-up-remarks/${id}`);
      setFollowUpRemarkDataById(response.data.data || []);
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setFollowUpRemarkDataById([]);
      } else {
        console.error("Error fetching follow Data:", err);
      }
    }
  };

  const openDonorFromDonation = (donorId) => {
    if (!donorId) {
      return;
    }

    navigate("/donor", {
      state: {
        openModal: true,
        donorId: donorId,
        activeTab: "Recurring donation",
        returnUrl: "/recurring/donations",
      },
    });
  };

  const openAddModal = () => {
    showModal("newUserModal");
    fetchCurrentDateAndTime();
  };

  const fetchReceiptDetails = async (id) => {
    try {
      const response = await axiosAPI.get(
        `/api/recurring-donation/download-donation-receipt/${id}`,
      );
      setReceiptDetails(response.data.user_details);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const openReceiptModal = (id) => {
    const selecteRecurrer = recurringDonations.find((donor) => donor.id === id);
    fetchReceiptDetails(id);
    setShowPdfModal(true);
  };

  return (
    <div className="container">
      <div className="page-inner">
        {role === "admin" && (
          <div className="d-flex justify-content-end align-items-end flex-column flex-md-row">
            <div className="me-2">
              <button
                type="button"
                className="btn btn-success btn-round mb-4"
                onClick={openAgencyWiseModal}
              >
                Export Recurring Donation
              </button>
            </div>
            <div className="me-2">
              <button
                type="button"
                className="btn btn-success btn-round mb-4"
                onClick={() => openUploadDonorModal()}
                disabled={donationLoading}
              >
                Upload Donation
              </button>
            </div>
            <div>
              <button
                type="button"
                disabled={donationLoading}
                className="btn btn-dark btn-round mb-4"
                onClick={() => openAddModal()}
              >
                Add Recurring Donation
              </button>
            </div>
          </div>
        )}

        {/* Modal */}
        <div
          className="modal fade "
          id="giftModal"
          tabIndex="-1"
          aria-labelledby="giftModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog  modal-dialog-centered">
            <div className="modal-content border">
              <div className="modal-header">
                <h5 className="modal-title" id="giftModalLabel">
                  {isFollowUpEditMode
                    ? "Edit Follow Up Remark"
                    : "Add Follow Up Remark"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={() => {
                    hideModal("giftModal");
                    setFollowUpRemarkDataById([]);
                  }}
                ></button>
              </div>
              <div className="modal-body shadow">
                <form onSubmit={handleFollowUpRemarkSubmit}>
                  <div className="mb-3">
                    <label htmlFor="followUpRemark" className="form-label">
                      Follow Up Remark
                    </label>

                    <textarea
                      className="form-control"
                      id="followUpRemark"
                      name="followUpRemark"
                      rows="3"
                      value={followUpRemarkData.followUpRemark}
                      onChange={followUpRemarkHandleChange}
                      required
                    ></textarea>
                  </div>
                  <hr />
                  {/* Follow Up Remark List */}
                  {followUpRemarkDataById.length > 0 ? (
                    <div className="table-responsive mb-3">
                      <table className="table table-bordered table-sm">
                        <thead className="table-dark">
                          <tr>
                            <th>#</th>
                            <th>Remark</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {followUpRemarkDataById.map((item, index) => (
                            <tr key={item.id}>
                              <td>{index + 1}</td>
                              <td>{item.description}</td>
                              <td>
                                {new Date(
                                  item?.created_at + "Z",
                                )?.toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-muted text-center mb-3">
                      No follow up remarks found
                    </p>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary btn-round btn-sm"
                  >
                    {isFollowUpEditMode
                      ? "Update Follow Up Remark"
                      : "Add Follow Up Remark"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Modal */}
        <div
          className="modal fade"
          id="agencyWiseModal"
          tabIndex="-1"
          aria-labelledby="newUserModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="newUserModalLabel">
                  {isEditMode ? "Export Donation" : "Export Donation"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleAgencyWiseSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Export Options</label>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        checked={formData.normal === "yes"}
                        id="normalExport"
                        onChange={() =>
                          setFormData({
                            normal: "yes",
                            agency_wise: "no",
                          })
                        }
                      />
                      <label
                        className="form-check-label ms-1"
                        htmlFor="normalExport"
                      >
                        Normal Download
                      </label>
                    </div>

                    <div className="form-check">
                      <input
                        type="checkbox"
                        checked={formData.agency_wise === "yes"}
                        id="agencyExport"
                        onChange={() =>
                          setFormData({
                            normal: "no",
                            agency_wise: "yes",
                          })
                        }
                      />
                      <label
                        className="form-check-label ms-1"
                        htmlFor="agencyExport"
                      >
                        Agency-wise Download
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-success btn-round btn-sm"
                  >
                    Export Donation
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div
          className="modal fade"
          id="followUpModal"
          tabIndex="-1"
          aria-labelledby="newUserModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="newUserModalLabel">
                  {isEditMode
                    ? "Export Follow Up Report "
                    : "Export Follow Up Report"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                ></button>
              </div>

              <div className="modal-body">
                <form onSubmit={handleFollowUpSubmit}>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="yearSelect">
                      Select Year
                    </label>
                    <select
                      className="form-select"
                      id="yearSelect"
                      value={followUpData.year}
                      onChange={(e) =>
                        setFollowUpData({
                          ...followUpData,
                          year: e.target.value,
                        })
                      }
                    >
                      <option value="">-- Select Year --</option>
                      {Array.from({ length: 10 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label" htmlFor="fromDate">
                      From Date
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="fromDate"
                      value={followUpData.from_date}
                      onChange={(e) =>
                        setFollowUpData({
                          ...followUpData,
                          from_date: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label" htmlFor="toDate">
                      To Date
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="toDate"
                      value={followUpData.to_date}
                      onChange={(e) =>
                        setFollowUpData({
                          ...followUpData,
                          to_date: e.target.value,
                        })
                      }
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-success btn-round btn-sm"
                  >
                    Export Follow Up Report
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Pdf Modal */}
        {showPdfModal && (
          <div
            className="modal show d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Receipt</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowPdfModal(false);
                      setReceiptDetails("");
                    }}
                  ></button>
                </div>
                <div className="modal-body pb-0" ref={receiptRef}>
                  <div className="row mb-3">
                    <div className="col-2">
                      <img
                        src={"/isckonkanpur-300x288-1.png"}
                        alt="ISKCON Logo"
                        className="img-fluid"
                        width="150px"
                      />
                    </div>
                    <div className="col-10 text-center">
                      <h1
                        style={{
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#a00",
                        }}
                      >
                        INTERNATIONAL SOCIETY FOR KRISHNA CONSCIOUSNESS (ISKCON)
                      </h1>
                      <h2
                        className="mb-0"
                        style={{ fontSize: "15px", fontWeight: "bold" }}
                      >
                        Founder Acharya: His Divine Grace A.C. Bhaktivedanta
                        Swami Srila Prabhupada
                      </h2>
                      <p style={{ fontSize: "14px", fontWeight: "bold" }}>
                        (Head Office: Hare Krishna Land, Juhu, Mumbai - 400 049)
                      </p>
                      <p className="mb-0" style={{ fontSize: "12px" }}>
                        Branch: Sri Sri Radha Madhav Mandir Mainavati Marg, Post
                        Kheora, Nawabganj Kanpur 208017. U.P. Ph: 7080007010
                      </p>
                      <p style={{ fontSize: "14px" }}>
                        Registered under Maharashtra Public Trust Act 1950, vide
                        Regn. No: F-2179(Bom). Unique Regn No. (80G):
                        AAATI0017PF20219
                      </p>
                    </div>
                  </div>

                  <hr />

                  <h2 className="text-center text-decoration-underline mb-3">
                    RECEIPT
                  </h2>

                  <div className="row">
                    <div className="col-12 mb-1">
                      <p className="mb-2">
                        <strong>Amount: </strong>
                        {receiptDetails.amount || " ---- "}
                      </p>
                      <p className="mb-2">
                        <strong>Amount in Words: </strong>
                        {receiptDetails.amountInWords || " ---- "}
                      </p>
                      <p className="mb-2">
                        <strong>Name: </strong>
                        {receiptDetails.full_name || " ---- "}
                      </p>
                      <p className="mb-2">
                        <strong>Email: </strong>
                        {receiptDetails.email || " ---- "}
                      </p>
                      <p className="mb-2">
                        <strong>Phone Number: </strong>
                        {receiptDetails?.phone_number || " ---- "}
                      </p>
                      <p className="mb-2">
                        <strong>Address: </strong>
                        {receiptDetails.address || " ---- "}
                      </p>
                      <p className="mb-2">
                        <strong>Pincode : </strong>
                        {receiptDetails?.zipcode || " ---- "}
                      </p>
                      <p className="mb-2">
                        <strong>Mode Of Payment: </strong>
                        {receiptDetails.mode}
                      </p>
                      <p className="mb-2">
                        <strong>Tr Number: </strong>
                        {receiptDetails?.tr_number || " ---- "}
                      </p>
                      <p className="mb-2">
                        <strong>Pan No: </strong>
                        {receiptDetails?.pan_no || " ---- "}
                      </p>
                      <p className="mb-2">
                        <strong>Date: </strong>
                        {receiptDetails.date
                          ? receiptDetails.date.split(" ")[0]
                          : ""}
                      </p>
                    </div>
                  </div>
                  <hr />
                  <div className="mt-4">
                    <p className="mb-1">
                      <h5>
                        Purpose of Donation:{" "}
                        {receiptDetails?.purpose_of_donation}
                      </h5>
                    </p>
                    <p className="mb-1">
                      <strong>Yours in the service of Lord Sri Krishna.</strong>
                    </p>
                    <p>
                      <em>
                        Note: This is a computer-generated receipt. No signature
                        is required.
                      </em>
                    </p>
                    <p className="text-danger font-weight-bold text-center">
                      Hare Krishna Hare Krishna Krishna Krishna Hare Hare Hare
                      Rama Hare Rama Rama Rama Hare Hare
                    </p>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-success"
                    onClick={downloadPdf}
                  >
                    Download PDF
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowPdfModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal */}
        <div
          className="modal fade"
          id="newUserModal"
          tabIndex="-1"
          aria-labelledby="newUserModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="newUserModalLabel">
                  {isEditMode
                    ? "Edit Recurring Donation"
                    : "Add Recurring Donation"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="user_id" className="form-label">
                      Select Donor
                    </label>
                    <AsyncSelect
                      cacheOptions
                      defaultOptions={filteredOptions}
                      loadOptions={loadOptions}
                      onChange={(selectedOption) =>
                        setFormData((prev) => ({
                          ...prev,
                          user_id: selectedOption,
                        }))
                      }
                      value={formData.user_id}
                      isClearable
                      placeholder="-- Select Donor --"
                    />
                  </div>

                  <div className="mb-3">
                    <label
                      htmlFor="transaction_date_and_time"
                      className="form-label"
                    >
                      Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      id="transaction_date_and_time"
                      name="transaction_date_and_time"
                      value={formData.transaction_date_and_time}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="amount" className="form-label">
                      Amount
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="amount"
                      name="amount"
                      placeholder="Enter Donation Amount"
                      value={formData.amount}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={donationLoading}
                    className={`btn btn-primary btn-round btn-sm ${donationLoading ? "spinner" : ""}`}
                  >
                    {isEditMode
                      ? "Update Recurring Donation"
                      : "Add Recurring Donation"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Add Donor Modal */}
        <div
          className="modal fade"
          id="newUserExcelModal"
          tabIndex="-1"
          aria-labelledby="newUserModalLabel"
          aria-hidden="true"
        >
          <div
            className={`modal-dialog ${
              view === "manual"
                ? "modal-fullscreen"
                : "modal-dialog-centered modal-dialog-scrollable modal-md"
            }`}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="newUserModalLabel">
                  {isEditMode ? "Edit Donation" : "Add Donation"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={() => {
                    closeModal();
                    setView("excel");
                  }}
                ></button>
              </div>

              <div className="modal-body">
                {view === "excel" && (
                  <>
                    <div className="mb-3">
                      <label htmlFor="excelUpload" className="form-label">
                        Upload Excel File (.xlsx)
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        id="excelUpload"
                        accept=".xlsx"
                        ref={fileInputRef}
                        onChange={handleExcelFileChange}
                      />

                      <div className="mt-3">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-primary"
                          onClick={() => {
                            const link = document.createElement("a");
                            link.href =
                              "/samples/auto_recurring_donation_sample.xlsx";
                            link.download =
                              "auto_recurring_donation_sample.xlsx";
                            link.click();
                          }}
                        >
                          Download Sample Sheet
                        </button>
                      </div>

                      <button
                        className="btn btn-success mt-3"
                        onClick={handleExcelSubmit}
                      >
                        Upload
                      </button>
                      {message && <div className="mt-2">{message}</div>}

                      {uploadRows.length > 0 && (
                        <div className="mt-3">
                          <h5>Upload Summary:</h5>
                          <table className="table table-bordered table-striped">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Row</th>
                                <th>Status</th>
                                <th>Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {uploadRows.map((rowData, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{rowData?.row}</td>
                                  <td
                                    style={{
                                      color:
                                        rowData?.status === "fail"
                                          ? "red"
                                          : "green",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {rowData?.status}
                                  </td>
                                  <td>{rowData?.reason || "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {downloading && <Loader />}

        {/* Recurring Donation Table */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                <h4 className="card-title mb-0">
                  All Recurring Donation ({recurringDonations.length})
                </h4>

                <div className="d-flex flex-wrap align-items-end gap-3">
                  {role === "admin" && (
                    <div
                      className="position-relative"
                      style={{ width: "320px" }}
                    >
                      <input
                        className="form-control shadow-sm px-3 py-2 border-2 w-100"
                        type="text"
                        placeholder="Enter name or phone_number or donor ID..."
                        value={filters.search}
                        onChange={(e) =>
                          handleFilterChange("search", e.target.value)
                        }
                        style={{ paddingRight: "110px" }}
                      />
                    </div>
                  )}

                  {role === "admin" && (
                    <div className="d-flex flex-column">
                      <label className="form-label mb-1">Agency</label>
                      <select
                        className="form-select"
                        value={filters.agency}
                        onChange={(e) =>
                          handleFilterChange("agency", e.target.value)
                        }
                        style={{ width: "200px" }}
                      >
                        <option value="" disabled>
                          ---Select Agency---
                        </option>
                        {!agenciesLoading &&
                          agencies.map((agency) => (
                            <option key={agency.name} value={agency.name}>
                              {agency.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}

                  <div className="d-flex flex-column">
                    <label htmlFor="fromDate" className="form-label mb-1">
                      From
                    </label>
                    <input
                      id="fromDate"
                      type="date"
                      className="form-control"
                      value={filters.from_date || ""}
                      onChange={(e) =>
                        handleFilterChange("from_date", e.target.value)
                      }
                    />
                  </div>

                  <div className="d-flex flex-column">
                    <label htmlFor="toDate" className="form-label mb-1">
                      To
                    </label>
                    <input
                      id="toDate"
                      type="date"
                      className="form-control"
                      value={filters.to_date || ""}
                      onChange={(e) =>
                        handleFilterChange("to_date", e.target.value)
                      }
                    />
                  </div>

                  <button
                    className="btn btn-primary rounded-pill px-4"
                    style={{ height: "38px" }}
                    onClick={() => {
                      if (
                        filters.search ||
                        filters.agency ||
                        filters.status ||
                        (filters.from_date && filters.to_date)
                      ) {
                        setPage(1);
                        recurringDonation();
                      } else {
                        alert("Please select both From and To dates.");
                      }
                    }}
                  >
                    Filter
                  </button>

                  <button
                    className="btn btn-icon btn-round btn-dark py-3 px-3"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title="Reset Filters"
                    onClick={handleResetFilters}
                  >
                    <i className="fas fa-sync-alt"></i>
                  </button>
                </div>
              </div>

              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Agency Name</th>
                        <th>Amount</th>
                        <th>Date</th>
                        <th>Tr no</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="text-center">
                            <Loader />
                          </td>
                        </tr>
                      ) : recurringDonations &&
                        recurringDonations.length > 0 ? (
                        recurringDonations.map((donation) => {
                          const formattedDate = new Date(
                            donation.transaction_date_and_time,
                          ).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          });

                          return (
                            <tr key={donation.id}>
                              <td>{donation.full_name}</td>
                              <td>{donation.processing_agency}</td>
                              <td>{donation.amount}</td>
                              <td>{formattedDate}</td>
                              <td>{donation.tr_no || "-"}</td>
                              <td>
                                {/* WhatsApp */}
                                {role === "admin" && (
                                  <a
                                    href={`https://wa.me/${donation?.phone_number}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-success btn-round btn-icon btn-sm me-2"
                                    title="WhatsApp"
                                  >
                                    <i className="fab fa-whatsapp"></i>
                                  </a>
                                )}
                                {/* Phone Call */}
                                {role === "admin" && (
                                  <a
                                    href={`tel:${donation?.phone_number}`}
                                    className="btn btn-dark btn-round btn-icon btn-sm me-2"
                                    title="Call"
                                  >
                                    <i className="fas fa-phone"></i>
                                  </a>
                                )}
                                <button
                                  className="btn btn-dark btn-round btn-icon btn-sm me-2"
                                  onClick={() => openReceiptModal(donation.id)}
                                >
                                  <i className="fas fa-download"></i>
                                </button>
                                {role === "admin" && (
                                  <button
                                    className="btn btn-dark btn-round btn-icon btn-sm me-2"
                                    onClick={() => openEditModal(donation.id)}
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                )}

                                {/* Follow Up Remark */}
                                {role === "admin" && (
                                  <button
                                    className="btn btn-dark btn-round btn-icon btn-sm me-2"
                                    onClick={() => {
                                      showModal("giftModal");
                                      setCurrentUserId(donation?.user_id);
                                      fetchFollowUpRemarkDataById(
                                        donation?.user_id,
                                      );
                                    }}
                                    title="Follow Up Remark"
                                  >
                                    <i className="fas fa-comment-dots"></i>
                                  </button>
                                )}

                                {role === "admin" && (
                                  <button
                                    className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                    onClick={() =>
                                      openDonorFromDonation(donation.user_id)
                                    }
                                  >
                                    <i className="fas fa-eye"></i>
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No Data Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <Pagination
              page={page}
              totalPages={totalPages}
              handlePageClick={handlePageClick}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default RecurringDonation;
